import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    withStyles,
    makeStyles,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    Typography,
    FormControlLabel,
    Checkbox,
    Grid,
    FormControl,
    TextField,
    TextareaAutosize,
    Select,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DeleteIcon from '@material-ui/icons/Delete';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { toastr } from 'react-redux-toastr';

import OrganisationsApi from '../../api/OrganisationsApi';

import { modalsButtonStyles } from '../../collums-constants/styles/stylesheets/buttonsStyles';
import { settingsStyles } from './styles';

import {
    COLLUMS_DOMAIN,
    COOKIE_KEYS,
    DEFAULT_PAYMENT_TYPES,
    DEFAULT_UNIT_VALUES,
    DEPOSIT_MULTISERVICE_RULE,
    DEPOSIT_RULE,
    GIFT_VOUCHER_AMOUNT
} from '../../collums-constants/index';
import DEFAULT_TREATMENT_PHOTOS_BY_GENDER from '../../collums-constants/treatmentImages';
import { AddPhoto } from '../../assets/icons';
import { defaultTC, defaultThankYouHtml } from '../../constants/CustomerAppBaseHtml';
import TreatmentImagesApi from '../../api/TreatmentImagesApi';

import LoadingScreen from '../../collums-components/components/common/loadingScreen';
import ConfirmModal from '../common/ConfirmModal';
import OpenedModal from '../common/OpenedModal';
import UploadPhotosModal from '../common/UploadPhotosModal';

import GeneralTab from './tabs/GeneralTab';
import PaymentTypesTab from './tabs/PaymentTypesTab';
import CalendarTab from './tabs/CalendarTab';
import CustomerAppTab, { DEPOSIT_TYPES, MINIMISE_GAPS_STRATEGY } from './tabs/CustomerAppTab';
import TreatmentRecordsTab from './tabs/TreatmentRecordsTab';
import ProductStockTab from './tabs/ProductStockTab';

import PaymentTypeModal from './PaymentTypeModal';
import ProductApi from './../../collums-components/api/ProductApi';
import { v4 } from 'uuid';
import TaxRatesTab from './tabs/TaxRatesTab';
import CommissionsTab from './tabs/CommissionsTab';
import CoherentSettingsTab from './tabs/CoherentSettingsTab';
import { listClinics } from '../../collums-components/api/ClinicApi';
import { useDispatch } from 'react-redux';
import { loadOrganisation } from '../../actions/organisationActions';
import ReportsSettings from './tabs/ReportsSettings';
import SecurityTab from './tabs/SecurityTab';
import { useCookies } from 'react-cookie';
import { TOKEN } from '../../collums-constants/storageKeys';
import AuthApi from '../../api/AuthApi';
import { getHasAdvancedPermissions } from '../../collums-components/helpers/user';

function Settings({ classes }) {
    const [expanded, setExpanded] = useState('');
    const [organisation, setOrganisation] = useState({});
    const [isVat0Only, setIsVat0Only] = useState(false);
    const globalStyles = makeStyles(modalsButtonStyles)();
    const [isLoading, setIsLoading] = useState(true);
    const [file, setFile] = useState(null);
    // used for comunication with the modal
    const [update, setUpdate] = useState(false);
    const [isPaymentTypeModalVisible, setIsPaymentTypeModalVisible] = useState(false);
    const [selectedType, setSelectedType] = useState();

    // const [rateFields, setRateFields] = useState({});

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [toxinProducts, setToxinProducts] = useState([]);
    const [treatmentRecords, setTreatmentRecords] = useState();
    const [bookIntervals, setBookIntervals] = useState(5);
    const [cancellationRules, setCancellationRules] = useState('allow');
    const [reschedulingRule, setReschedulingRule] = useState('allow');
    const [bookingDepositMultipleServicesRule, setBookingDepositMultipleServicesRule] = useState(
        DEPOSIT_MULTISERVICE_RULE.SUM
    );
    const [giftVoucherExpiryPeriod, setGiftVoucherExpiryPeriod] = useState('years');
    const [giftVoucherExpiryValue, setGiftVoucherExpiryValue] = useState(1);
    const [bookingDepositRule, setBookingDepositRule] = useState(DEPOSIT_RULE.EACH_TIME);
    const [treatmentPhotos, setTreatmentPhotos] = useState(DEFAULT_TREATMENT_PHOTOS_BY_GENDER.ALL);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [deleteImageModalOpen, setDeleteImageModalOpen] = useState(false);
    const [imageIndexToDelete, setImageIndexToDelete] = useState(null);
    const [, setCookies] = useCookies([TOKEN]);
    const [clinics, setClinics] = useState([]);

    const dispatch = useDispatch();

    // refs for the customer app texts
    const homeBannerRef = useRef();
    const termsAndConditionsRef = useRef();
    const lateCancellationRef = useRef();
    const thankYouRef = useRef();
    const termsAndConditionsCourseRef = useRef();
    const thankYouCourseRef = useRef();
    const termsAndConditionsCourseServiceRef = useRef();
    const thankYouCourseServiceRef = useRef();
    const termsAndConditionsGiftVoucherRef = useRef();
    const thankYouGiftVoucherRef = useRef();
    const treatmentRecordsRef = useRef();

    const [hasAdvancedPermissions, setHasAdvancedPermissions] = useState(false);

    const { register, handleSubmit, setValue, errors, control, reset, watch } = useForm();

    const { fields, append } = useFieldArray({
        control,
        name: 'paymentTypes'
    });

    const getNewTreatmentRecord = useCallback(() => {
        return {
            id: v4(),
            doseUnits: [...DEFAULT_UNIT_VALUES]
        };
    }, []);

    useEffect(() => {
        AuthApi.getMe().then(res => {
            setHasAdvancedPermissions(getHasAdvancedPermissions(res));
        });
    }, []);

    const changeAllowSkippingFormsInJourney = useCallback(
        e => {
            setOrganisation({
                ...organisation,
                allowSkippingFormsInJourney: e.target.checked
            });
        },
        [organisation, setOrganisation]
    );

    const changeAutoCloseLeadOnBooking = useCallback(
        e => {
            setOrganisation({
                ...organisation,
                autoCloseLeadOnBooking: e.target.checked
            });
        },
        [organisation, setOrganisation]
    );

    const resetTreatmentRecordsData = useCallback(
        res => {
            ProductApi.getByTagType('toxin').then(toxins => {
                treatmentRecordsRef.current = (res.injectionDoses || [])
                    .map(injectionDose => {
                        const toxin = toxins.find(toxin => toxin.id === injectionDose.toxin);
                        return {
                            ...injectionDose,
                            toxin
                        };
                    })
                    .filter(injectionDose => injectionDose.toxin);

                if (!treatmentRecordsRef.current.length) {
                    treatmentRecordsRef.current = [getNewTreatmentRecord()];
                }

                setTreatmentRecords(treatmentRecordsRef.current);

                setToxinProducts(toxins);
            });
        },
        [setToxinProducts, setTreatmentRecords, treatmentRecordsRef, getNewTreatmentRecord]
    );

    const addNewTreatmentRecord = () => setTreatmentRecords([...treatmentRecordsRef.current, getNewTreatmentRecord()]);

    const loadTreatmentImages = useCallback(async () => {
        try {
            const images = await TreatmentImagesApi.list();
            // Merge default images with uploaded images
            setTreatmentPhotos([...DEFAULT_TREATMENT_PHOTOS_BY_GENDER.ALL, ...images]);
        } catch (error) {
            console.error('Error loading treatment images:', error);
            // Fallback to default images if API fails
            setTreatmentPhotos(DEFAULT_TREATMENT_PHOTOS_BY_GENDER.ALL);
        }
    }, []);

    const confirmDeleteImage = useCallback(index => {
        setImageIndexToDelete(index);
        setDeleteImageModalOpen(true);
    }, []);

    const handleDeleteTreatmentPhoto = useCallback(async () => {
        if (imageIndexToDelete === null) return;

        const photo = treatmentPhotos[imageIndexToDelete];
        if (!photo.id) {
            // If no ID, just remove from local state (default image)
            const updatedPhotos = treatmentPhotos.filter((_, i) => i !== imageIndexToDelete);
            setTreatmentPhotos(updatedPhotos);
            setDeleteImageModalOpen(false);
            setImageIndexToDelete(null);
            return;
        }

        try {
            setIsLoading(true);
            await TreatmentImagesApi.delete(photo.id);
            const updatedPhotos = treatmentPhotos.filter((_, i) => i !== imageIndexToDelete);
            setTreatmentPhotos(updatedPhotos);
            toastr.success('Success', 'Image deleted successfully!');
        } catch (error) {
            console.error('Error deleting treatment image:', error);
            toastr.error('Error', 'Failed to delete image. Please try again.');
        } finally {
            setIsLoading(false);
            setDeleteImageModalOpen(false);
            setImageIndexToDelete(null);
        }
    }, [treatmentPhotos, imageIndexToDelete]);

    const handlePhotoUpload = useCallback(
        async uploadedPhotos => {
            try {
                setIsLoading(true);
                const uploadPromises = uploadedPhotos.map(async photo => {
                    // uploadImage handles: create record, upload to S3, process PDF if needed
                    const dbRecord = await TreatmentImagesApi.uploadImage(photo.file, photo.name);
                    return dbRecord;
                });

                const newPhotos = await Promise.all(uploadPromises);
                setTreatmentPhotos([...treatmentPhotos, ...newPhotos]);
                setShowUploadModal(false);
                toastr.success('Success', 'Images uploaded successfully!');
            } catch (error) {
                console.error('Error uploading treatment images:', error);
                toastr.error('Error', 'Failed to upload images. Please try again.');
            } finally {
                setIsLoading(false);
            }
        },
        [treatmentPhotos]
    );

    const loadData = useCallback(
        fromSave => {
            setIsLoading(true);

            // Load clinics
            listClinics()
                .then(clinicsData => {
                    setClinics(clinicsData || []);
                })
                .catch(err => {
                    console.error('Error loading clinics:', err);
                });

            OrganisationsApi.getOrganisation()
                .then(res => {
                    if (fromSave) {
                        return;
                    }
                    resetTreatmentRecordsData(res);
                    setOrganisation({
                        ...res,
                        ...(res.termsAndConditionsText ? {} : { termsAndConditionsText: defaultTC }),
                        ...(res.thankYouText ? {} : { thankYouText: defaultThankYouHtml }),
                        ...(res.termsAndConditionsTextCourse ? {} : { termsAndConditionsTextCourse: defaultTC }),
                        ...(res.thankYouTextCourse ? {} : { thankYouTextCourse: defaultThankYouHtml }),
                        ...(res.termsAndConditionsTextCourseService
                            ? {}
                            : { termsAndConditionsTextCourseService: defaultTC }),
                        ...(res.thankYouTextCourseService ? {} : { thankYouTextCourseService: defaultThankYouHtml }),
                        ...(res.termsAndConditionsTextGiftVoucher
                            ? {}
                            : { termsAndConditionsTextGiftVoucher: defaultTC }),
                        ...(res.thankYouTextGiftVoucher ? {} : { thankYouTextGiftVoucher: defaultThankYouHtml }),
                        ...(!res.paymentTypes ||
                        res.paymentTypes.length === 0 ||
                        typeof res.paymentTypes[0] === 'string' ||
                        !res.paymentTypes[0].label
                            ? { paymentTypes: DEFAULT_PAYMENT_TYPES }
                            : { paymentTypes: res.paymentTypes }),
                        ...(!res?.bookingDepositType
                            ? { bookingDepositType: DEPOSIT_TYPES.PERCENTAGE }
                            : { bookingDepositType: res.bookingDepositType }),
                        bookingDeposit: !!(res?.bookingDepositType && res?.bookingDepositAmount !== ''),
                        giftVoucherValues: res.giftVoucherValues.join('\n') || GIFT_VOUCHER_AMOUNT.join('\n')
                    });
                    setBookIntervals(res.bookIntervals);
                    if (res.cancellationRules) {
                        setCancellationRules(res.cancellationRules);
                    }
                    if (res.reschedulingRule) {
                        setReschedulingRule(res.reschedulingRule);
                    } else if (res.cancellationRules) {
                        setReschedulingRule(res.cancellationRules);
                    }
                    if (res.bookingDepositMultipleServicesRule) {
                        setBookingDepositMultipleServicesRule(res.bookingDepositMultipleServicesRule);
                    }
                    if (res.bookingDepositRule) {
                        setBookingDepositRule(res.bookingDepositRule);
                    }

                    if (res.isVat0Only) {
                        setIsVat0Only(true);
                    }

                    reset({
                        ...res,
                        ...(res.termsAndConditionsText ? {} : { termsAndConditionsText: defaultTC }),
                        ...(res.thankYouText ? {} : { thankYouText: defaultThankYouHtml }),
                        ...(res.termsAndConditionsTextCourse ? {} : { termsAndConditionsTextCourse: defaultTC }),
                        ...(res.thankYouTextCourse ? {} : { thankYouTextCourse: defaultThankYouHtml }),
                        ...(res.termsAndConditionsTextCourseService
                            ? {}
                            : { termsAndConditionsTextCourseService: defaultTC }),
                        ...(res.thankYouTextCourseService ? {} : { thankYouTextCourseService: defaultThankYouHtml }),
                        ...(res.termsAndConditionsTextGiftVoucher
                            ? {}
                            : { termsAndConditionsTextGiftVoucher: defaultTC }),
                        ...(res.thankYouTextGiftVoucher ? {} : { thankYouTextGiftVoucher: defaultThankYouHtml }),
                        ...(!res.paymentTypes ||
                        res.paymentTypes.length === 0 ||
                        typeof res.paymentTypes[0] === 'string' ||
                        !res.paymentTypes[0].label
                            ? { paymentTypes: DEFAULT_PAYMENT_TYPES }
                            : { paymentTypes: res.paymentTypes })
                    });
                })
                .finally(() => setIsLoading(false));
        },
        [reset, resetTreatmentRecordsData]
    );

    useEffect(() => {
        loadData();
        loadTreatmentImages();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        const requiredFields = Object.keys(errors);

        if (requiredFields.length !== 0) {
            const messages = requiredFields.map(field => errors[field].message);

            toastr.error('Missing fields', messages.join(','));
        }
    }, [errors]);

    const handleCancel = () => {
        // clear fields (load from api)
        setIsLoading(true);
        OrganisationsApi.getOrganisation().then(res => {
            resetTreatmentRecordsData(res);
            setOrganisation(res);
            reset(res);
            setIsLoading(false);
        });
    };

    const mapValuesToObject = useCallback(
        async paymentTypes => {
            return await paymentTypes
                .map(paymentType => {
                    const key = Object.keys(paymentType)[0];
                    const temp = fields.find(field => field.name === key);
                    if (temp?.id) delete temp.id;
                    return temp;
                })
                .filter(paymentType => paymentType);
        },
        [fields]
    );

    const convertDoseUnits = doseUnits => {
        return doseUnits.map(doseUnit => {
            return Math.round(parseFloat(doseUnit) * 100) / 100;
        });
    };

    const onSubmit = useCallback(
        async values => {
            setTreatmentRecords(treatmentRecordsRef.current);

            const injectionDoses = (treatmentRecordsRef.current || [])
                .filter(el => el.toxin)
                .map(el => ({ toxin: el.toxin.id, doseUnits: convertDoseUnits(el.doseUnits) }));

            const isTreatmentRecordsWithError = (injectionDoses || []).some(el => {
                return el.doseUnits.some(unit => {
                    return !unit || unit < 0.1 || Number.isNaN(unit);
                });
            });

            const giftVouchersValueslines = (values.giftVoucherValues || '').split(/\r?\n/).filter(line => line.trim());
            if (!giftVouchersValueslines.every(line => /^\d+$/.test(line.trim()))) {
                toastr.error('Gift voucher values should only contain numbers');
                return;
            }
            values.giftVoucherValues = giftVouchersValueslines;

            if (
                values?.minimiseGaps &&
                values?.minimiseGapsStrategy === MINIMISE_GAPS_STRATEGY.ALWAYS_MINIMISE &&
                !(values?.alsoShowFirst || values?.alsoShowLast)
            ) {
                toastr.error('Select First or Last slot of the day to avoid no availability');
                return;
            }

            if (isTreatmentRecordsWithError) {
                toastr.error('Invalid unit values on injection dose values');
                return;
            }

            if (!(values?.name || '').trim()) {
                toastr.error('Missing name');
                return;
            }

            if (!(values?.email || '').trim()) {
                toastr.error('Missing email address');
                return;
            }

            if (!(values?.emailName || '').trim()) {
                toastr.error('Missing email send from name');
                return;
            }

            if (!(values?.address?.line1 || '').trim()) {
                toastr.error('Missing address1');
                return;
            }

            if (!(values?.address?.postcode || '').trim()) {
                toastr.error('Missing postcode');
                return;
            }

            if (!(values?.address?.city || '').trim()) {
                toastr.error('Missing city');
                return;
            }

            if (!(values?.address?.country || '').trim()) {
                toastr.error('Missing country');
                return;
            }

            if (values?.bookingDeposit) {
                // if (Number(values?.bookingDepositAmount) === 0) {
                //     toastr.error('Please provide correct value for Deposit percentage/amount');
                //     return;
                // }
                if (
                    Number(values?.bookingDepositAmount) > 100 &&
                    values?.bookingDepositType === DEPOSIT_TYPES.PERCENTAGE
                ) {
                    values.bookingDepositAmount = 100;
                }
                values.bookingDepositAmount = Number(values.bookingDepositAmount);
            } else {
                values.bookingDepositAmount = null;
                values.bookingDepositType = '';
            }

            delete values.bookingDeposit;

            setIsLoading(true);
            if (file) {
                //save logo
                await OrganisationsApi.uploadOrganisationLogo(organisation.id, file);
            }

            // saves only if user has changed the text
            if (values.termsAndConditionsText === defaultTC) {
                delete values.termsAndConditionsText;
            }
            if (values.thankYouText === defaultThankYouHtml) {
                delete values.thankYouText;
            }
            if (values.termsAndConditionsTextCourse === defaultTC) {
                delete values.termsAndConditionsTextCourse;
            }
            if (values.thankYouTextCourse === defaultThankYouHtml) {
                delete values.thankYouTextCourse;
            }
            if (values.termsAndConditionsTextCourseService === defaultTC) {
                delete values.termsAndConditionsTextCourseService;
            }
            if (values.thankYouTextCourseService === defaultThankYouHtml) {
                delete values.thankYouTextCourseService;
            }
            if (values.termsAndConditionsTextGiftVoucher === defaultTC) {
                delete values.termsAndConditionsTextGiftVoucher;
            }
            if (values.thankYouTextGiftVoucher === defaultThankYouHtml) {
                delete values.thankYouTextGiftVoucher;
            }

            const data = {
                ...values,
                paymentTypes: await mapValuesToObject(values.paymentTypes),
                homeBannerText: homeBannerRef.current?.value || undefined,
                termsAndConditionsText: termsAndConditionsRef.current?.value || undefined,
                lateCancellationText: lateCancellationRef.current?.value || '',
                thankYouText: thankYouRef.current?.value || undefined,
                termsAndConditionsTextCourse: termsAndConditionsCourseRef.current?.value || undefined,
                thankYouTextCourse: thankYouCourseRef.current?.value || undefined,
                termsAndConditionsTextCourseService: termsAndConditionsCourseServiceRef.current?.value || undefined,
                thankYouTextCourseService: thankYouCourseServiceRef.current?.value || undefined,
                termsAndConditionsTextGiftVoucher: termsAndConditionsGiftVoucherRef.current?.value || undefined,
                thankYouTextGiftVoucher: thankYouGiftVoucherRef.current?.value || undefined,
                injectionDoses,
                reporting: organisation.reporting || {},
                isVat0Only,
                coherentClinicKey: organisation.coherentClinicKey || [],
                terminals: organisation.terminals || []
            };

            const reporting = data.reporting || {};
            if (reporting.kpiWeeklyEnabled && !reporting.kpiWeeklyRecipients?.trim()) {
                setIsLoading(false);
                return toastr.error('Please enter recipient emails for the weekly KPI report');
            }
            if (reporting.kpiMonthlyEnabled && !reporting.kpiMonthlyRecipients?.trim()) {
                setIsLoading(false);
                return toastr.error('Please enter recipient emails for the monthly KPI report');
            }

            OrganisationsApi.updateOrganisation(data)
                .then(async () => {
                    if (data.logoutTime && data.logoutActive !== undefined) {
                        const environment = {};
                        if (window.location.hostname !== 'localhost') {
                            environment.path = '/';
                            environment.domain = COLLUMS_DOMAIN;
                        }
                        setCookies(COOKIE_KEYS.EXP_TIME, data.logoutTime, environment);
                        setCookies(COOKIE_KEYS.LOGOUT_ACTIVE, data.logoutActive, environment);
                    }

                    dispatch(loadOrganisation(data));
                    localStorage.setItem('currency', data.currency);
                    await loadData(true);
                    setIsLoading(false);
                    toastr.success('Organisation settings updated');
                })
                .catch(err => {
                    setIsLoading(false);
                    reset();
                    if (typeof err === 'object') {
                        if (err.data && err.data.message) {
                            toastr.error(err.data.message);
                            return;
                        }
                        toastr.error('Oops', 'Something went wrong');
                    }
                });
        },
        // eslint-disable-next-line
        [
            file,
            loadData,
            mapValuesToObject,
            organisation.id,
            organisation.reporting,
            isVat0Only,
            organisation.coherentClinicKey,
            organisation.terminals
        ]
    );

    const submit = useCallback(
        e => {
            if (e) {
                e.preventDefault();
            }

            handleSubmit(onSubmit)();
        },
        [handleSubmit, onSubmit]
    );

    // saves the organisation when updated on paymentTypeModal as we cannot pass the submit func
    useEffect(() => {
        if (update) {
            submit();
            setUpdate(false);
        }
    }, [submit, update]);

    const updatePaymentType = useCallback(
        async (id, newOption) => {
            let newPayments = await (fields || []).map(payment => {
                if (payment.id === id) {
                    return { ...newOption };
                }
                return payment;
            });

            setValue('paymentTypes', newPayments);
            setOrganisation({
                ...organisation,
                paymentTypes: newPayments
            });
        },
        [fields, organisation, setValue]
    );

    const openPaymentsModal = useCallback(paymentType => {
        if (paymentType && paymentType.id) {
            setSelectedType(paymentType);
            setIsPaymentTypeModalVisible(true);
        } else {
            setSelectedType('new');
            setIsPaymentTypeModalVisible(true);
        }
    }, []);

    const openDeleteModal = useCallback(paymentType => {
        setSelectedType(paymentType);
        setDeleteModalOpen(true);
    }, []);

    const ClosePaymentTypeModal = () => {
        setSelectedType();
        setDeleteModalOpen(false);
        setIsPaymentTypeModalVisible(false);
    };

    const handleDeletePaymentType = useCallback(async () => {
        if (selectedType.id) {
            await OrganisationsApi.removePaymentType(organisation.id, selectedType.id);
            await loadData();
        }
    }, [loadData, organisation, selectedType]);

    if (isLoading && !Object.keys(organisation).length) {
        return <LoadingScreen />;
    }

    return (
        <OpenedModal>
            <form onSubmit={submit}>
                {/* Label options  // hardcoded for now
                <Accordion
                    expanded={expanded === 'labelOptions'}
                    onChange={() => (expanded === 'labelOptions' ? setExpanded('') : setExpanded('labelOptions'))}
                >
                    <AccordionSummary>
                        <Typography>Label options</Typography>
                    </AccordionSummary>
                    <AccordionDetails></AccordionDetails>
                </Accordion>*/}

                {/* Calendar */}
                <Accordion
                    expanded={expanded === 'calendar'}
                    onChange={() => setExpanded(expanded === 'calendar' ? '' : 'calendar')}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.9rem' }} />}>
                        <Typography>Calendar</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <CalendarTab
                            register={register}
                            organisation={organisation}
                            control={control}
                            setOrganisation={setOrganisation}
                        />
                    </AccordionDetails>
                </Accordion>

                <Accordion
                    expanded={expanded === 'treatmentRecords'}
                    onChange={() => setExpanded(expanded === 'treatmentRecords' ? '' : 'treatmentRecords')}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.9rem' }} />}>
                        <Typography>Client Journey and Treatment Records</Typography>
                    </AccordionSummary>
                    <AccordionDetails style={{ flexDirection: 'column' }}>
                        <Typography variant="h4">Client journey</Typography>

                        <Grid container direction="column" spacing={3}>
                            <Grid item>
                                <FormControlLabel
                                    label="Allow to skip forms in journey"
                                    control={
                                        <Checkbox
                                            onChange={changeAllowSkippingFormsInJourney}
                                            color="primary"
                                            checked={Boolean(organisation?.allowSkippingFormsInJourney)}
                                            inputRef={register}
                                            name="allowSkippingFormsInJourney"
                                        />
                                    }
                                />
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                    <AccordionDetails>
                        {treatmentRecords && (
                            <TreatmentRecordsTab
                                treatmentRecordsRef={treatmentRecordsRef}
                                toxinProducts={toxinProducts}
                                treatmentRecords={treatmentRecords}
                                setTreatmentRecords={setTreatmentRecords}
                                addNewTreatmentRecord={addNewTreatmentRecord}
                            />
                        )}
                    </AccordionDetails>
                </Accordion>

                <Accordion
                    expanded={expanded === 'treatmentImages'}
                    onChange={() => setExpanded(expanded === 'treatmentImages' ? '' : 'treatmentImages')}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.9rem' }} />}>
                        <Typography>Treatment Images</Typography>
                    </AccordionSummary>
                    <AccordionDetails style={{ flexDirection: 'column' }}>
                        <Grid container direction="column" spacing={3}>
                            <Grid item>
                                <Button
                                    className={globalStyles.confirmButton}
                                    startIcon={
                                        <span style={{ filter: 'brightness(0) invert(1)' }}>
                                            <AddPhoto variant="special" />
                                        </span>
                                    }
                                    onClick={() => setShowUploadModal(true)}
                                >
                                    Upload Photo/PDF
                                </Button>
                            </Grid>
                            <Grid item>
                                <TableContainer component={Paper}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Image</TableCell>
                                                <TableCell>Name</TableCell>
                                                <TableCell align="right">Action</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {treatmentPhotos.map((photo, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>
                                                        <img
                                                            src={photo.url}
                                                            alt={photo.name}
                                                            style={{
                                                                width: '100px',
                                                                height: 'auto',
                                                                objectFit: 'contain'
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>{photo.name}</TableCell>
                                                    <TableCell align="right">
                                                        {photo.id && (
                                                            <IconButton
                                                                color="secondary"
                                                                onClick={() => confirmDeleteImage(index)}
                                                                aria-label="delete"
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>

                {/* Commissions  */}
                <Accordion
                    expanded={expanded === 'commissions'}
                    onChange={() => setExpanded(expanded === 'commissions' ? '' : 'commissions')}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.9rem' }} />}>
                        <Typography>Commissions</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <CommissionsTab expanded={expanded === 'commissions'} setIsLoading={setIsLoading} />
                    </AccordionDetails>
                </Accordion>

                <Accordion expanded={expanded === 'gtm'} onChange={() => setExpanded(expanded === 'gtm' ? '' : 'gtm')}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.9rem' }} />}>
                        <Typography>Google Tag Manager</Typography>
                    </AccordionSummary>
                    <AccordionDetails style={{ flexDirection: 'column' }}>
                        <Typography variant="h4" style={{ marginBottom: 10 }}>
                            Connect Google Tag Manager with your online booking page
                        </Typography>

                        <Grid container direction="column" spacing={3} xs={6}>
                            <Grid item>
                                <FormControl className={classes.fieldContainer}>
                                    <TextField
                                        id="gtmId"
                                        label="GTM ID"
                                        variant="outlined"
                                        name="gtmId"
                                        placeholder="GTM-XXXXXXX"
                                        inputRef={register()}
                                        value={organisation.gtmId ? organisation.gtmId : ''}
                                        onChange={({ target }) =>
                                            setOrganisation({
                                                ...organisation,
                                                gtmId: target.value
                                            })
                                        }
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                        <Typography variant="h4" style={{ marginBottom: 10, marginTop: 20 }}>
                            Thank you page redirect
                        </Typography>
                        <div style={{ marginBottom: 10 }}>
                            Redirect your customer to a unique thank you page on completion of an online booking. This
                            is instead of seeing the Collums&apos; thank you page. Don’t forget to include https:// at
                            the start or your url.
                        </div>
                        <Grid container direction="column" spacing={3} xs={9}>
                            <Grid item>
                                <FormControl className={classes.fieldContainer}>
                                    <TextField
                                        id="thankYouPageUrl"
                                        label="Custom thank you page url"
                                        variant="outlined"
                                        name="thankYouPageUrl"
                                        placeholder="https://yourpage"
                                        inputRef={register()}
                                        value={organisation.thankYouPageUrl ? organisation.thankYouPageUrl : ''}
                                        onChange={({ target }) =>
                                            setOrganisation({
                                                ...organisation,
                                                thankYouPageUrl: target.value
                                            })
                                        }
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                        <span>
                            Once connected, please check your Google Tag Manager is set up correctly and receiving the
                            data you expect. Seek the advice of a professional if you are unsure.
                        </span>
                    </AccordionDetails>
                </Accordion>

                <Accordion
                    expanded={expanded === 'giftvoucher'}
                    onChange={() => setExpanded(expanded === 'giftvoucher' ? '' : 'giftvoucher')}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.9rem' }} />}>
                        <Typography>Gift Vouchers</Typography>
                    </AccordionSummary>
                    <AccordionDetails style={{ flexDirection: 'column' }}>
                        <Grid container direction="column" spacing={3} xs={6}>
                            <Grid item>
                                <div className={classes.inlineContainer}>
                                    <FormControlLabel
                                        label="Sell gift vouchers online"
                                        control={
                                            <Checkbox
                                                onChange={e => {
                                                    setOrganisation({
                                                        ...organisation,
                                                        sellGiftVoucherOnline: e.target.checked
                                                    });
                                                }}
                                                color="primary"
                                                checked={Boolean(organisation?.sellGiftVoucherOnline)}
                                                inputRef={register}
                                                name="sellGiftVoucherOnline"
                                            />
                                        }
                                    />
                                </div>
                            </Grid>
                            <Grid item>
                                <Typography
                                    className={organisation?.sellGiftVoucherOnline ? '' : ' ' + classes.disabled}
                                >
                                    Enter the values of gift vouchers to be offered online. Put a single value on each
                                    line in the order that you wish them to appear.
                                </Typography>
                            </Grid>
                            <Grid item>
                                <div className={classes.inlineContainer}>
                                    <FormControl
                                        variant="h4"
                                        className={organisation?.sellGiftVoucherOnline ? '' : ' ' + classes.disabled}
                                    >
                                        Gift voucher values for sale online
                                    </FormControl>
                                </div>
                                <FormControl style={{ width: 400 }}>
                                    <TextareaAutosize
                                        id="giftVoucherValues"
                                        name="giftVoucherValues"
                                        disabled={!organisation?.sellGiftVoucherOnline}
                                        minRows={3}
                                        className={classes.textArea}
                                        ref={register()}
                                        value={organisation.giftVoucherValues || ''}
                                        onChange={({ target }) =>
                                            setOrganisation({
                                                ...organisation,
                                                giftVoucherValues: target.value
                                            })
                                        }
                                        style={{
                                            width: '100%',
                                            maxWidth: '100%',
                                            boxSizing: 'border-box',
                                            minHeight: '100px',
                                            fontFamily: 'Roboto, Arial, Sans-serif'
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item>
                                <FormControl
                                    className={classes.fieldContainer}
                                    variant="outlined"
                                    style={{ marginBottom: 16 }}
                                >
                                    <div className={`${classes.inlineContainer} ${classes.intervalLabel}`}>
                                        <Typography>Default gift voucher expiry</Typography>
                                        &nbsp;
                                        <Controller
                                            name="giftVoucherExpiryValue"
                                            defaultValue={giftVoucherExpiryValue}
                                            control={control}
                                            as={
                                                <Select onChange={() => setGiftVoucherExpiryValue()}>
                                                    {[...Array(18)].map((_, i) => (
                                                        <MenuItem key={i + 1} value={i + 1}>
                                                            {i + 1}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            }
                                        />
                                        &nbsp;
                                        <Controller
                                            name="giftVoucherExpiryPeriod"
                                            defaultValue={giftVoucherExpiryPeriod}
                                            control={control}
                                            as={
                                                <Select onChange={() => setGiftVoucherExpiryPeriod()}>
                                                    <MenuItem key="years" value="years">
                                                        Years
                                                    </MenuItem>
                                                    <MenuItem key="months" value="months">
                                                        Months
                                                    </MenuItem>
                                                </Select>
                                            }
                                        />
                                    </div>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>

                {/* Online Customer App */}
                <Accordion
                    expanded={expanded === 'customerApp'}
                    onChange={() => setExpanded(expanded === 'customerApp' ? '' : 'customerApp')}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.9rem' }} />}>
                        <Typography>Online Booking & Deposits</Typography>
                    </AccordionSummary>
                    <AccordionDetails className={classes.accordionDetails}>
                        <CustomerAppTab
                            register={register}
                            bookIntervals={bookIntervals}
                            setBookIntervals={setBookIntervals}
                            cancellationRules={cancellationRules}
                            setCancellationRules={setCancellationRules}
                            reschedulingRule={reschedulingRule}
                            setReschedulingRule={setReschedulingRule}
                            bookingDepositMultipleServicesRule={bookingDepositMultipleServicesRule}
                            setBookingDepositMultipleServicesRule={setBookingDepositMultipleServicesRule}
                            bookingDepositRule={bookingDepositRule}
                            setBookingDepositRule={setBookingDepositRule}
                            organisation={organisation}
                            control={control}
                            setValue={setValue}
                            setOrganisation={setOrganisation}
                            homeBannerRef={homeBannerRef}
                            termsAndConditionsRef={termsAndConditionsRef}
                            lateCancellationRef={lateCancellationRef}
                            thankYouRef={thankYouRef}
                            termsAndConditionsCourseRef={termsAndConditionsCourseRef}
                            thankYouCourseRef={thankYouCourseRef}
                            termsAndConditionsCourseServiceRef={termsAndConditionsCourseServiceRef}
                            thankYouCourseServiceRef={thankYouCourseServiceRef}
                            termsAndConditionsGiftVoucherRef={termsAndConditionsGiftVoucherRef}
                            thankYouGiftVoucherRef={thankYouGiftVoucherRef}
                            watch={watch}
                        />
                    </AccordionDetails>
                </Accordion>

                {/* General */}
                <Accordion
                    expanded={expanded === 'general'}
                    onChange={() => setExpanded(expanded === 'general' ? '' : 'general')}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.9rem' }} />}>
                        <Typography>Organisation Details</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <GeneralTab
                            register={register}
                            organisation={organisation}
                            setValue={setValue}
                            setOrganisation={setOrganisation}
                            setFile={setFile}
                        />
                    </AccordionDetails>
                </Accordion>

                {/* Payment Types  */}
                <Accordion
                    expanded={expanded === 'paymentTypes'}
                    onChange={() => setExpanded(expanded === 'paymentTypes' ? '' : 'paymentTypes')}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.9rem' }} />}>
                        <Typography>Payments</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <PaymentTypesTab
                            fields={fields}
                            setValue={setValue}
                            register={register}
                            organisation={organisation}
                            setOrganisation={setOrganisation}
                            openModal={openPaymentsModal}
                            openDeleteModal={openDeleteModal}
                            setUpdate={setUpdate}
                            hasAdvancedPermissions={hasAdvancedPermissions}
                        />
                    </AccordionDetails>
                </Accordion>

                {/* Product Stock settings */}
                <Accordion
                    expanded={expanded === 'productStock'}
                    onChange={() => setExpanded(expanded === 'productStock' ? '' : 'productStock')}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.9rem' }} />}>
                        <Typography>Product Stock</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <ProductStockTab
                            register={register}
                            organisation={organisation}
                            control={control}
                            setValue={setValue}
                            setOrganisation={setOrganisation}
                        />
                    </AccordionDetails>
                </Accordion>

                {/* Tax Rates  */}
                <Accordion
                    expanded={expanded === 'taxRates'}
                    onChange={() => setExpanded(expanded === 'taxRates' ? '' : 'taxRates')}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.9rem' }} />}>
                        <Typography>Tax Rates</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TaxRatesTab
                            expanded={expanded === 'taxRates'}
                            organisationId={organisation.id}
                            setIsLoading={setIsLoading}
                            isVat0Only={isVat0Only}
                            setIsVat0Only={setIsVat0Only}
                            // openModal={openTaxRatesModal}
                        />
                    </AccordionDetails>
                </Accordion>

                {/* Coherent Settings */}
                <Accordion
                    expanded={expanded === 'coherentSettings'}
                    onChange={() => setExpanded(expanded === 'coherentSettings' ? '' : 'coherentSettings')}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.9rem' }} />}>
                        <Typography>Coherent Settings</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <CoherentSettingsTab
                            clinics={clinics}
                            coherentClinicKey={organisation.coherentClinicKey || []}
                            setCoherentClinicKey={value =>
                                setOrganisation({ ...organisation, coherentClinicKey: value })
                            }
                            terminals={organisation.terminals || []}
                            setTerminals={value => setOrganisation({ ...organisation, terminals: value })}
                        />
                    </AccordionDetails>
                </Accordion>

                {/* Leads */}
                <Accordion
                    expanded={expanded === 'leads'}
                    onChange={() => setExpanded(expanded === 'leads' ? '' : 'leads')}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.9rem' }} />}>
                        <Typography>Leads</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <FormControlLabel
                            label='Automatically update lead status to "Closed - Won" when lead appointment is booked'
                            control={
                                <Checkbox
                                    onChange={changeAutoCloseLeadOnBooking}
                                    color="primary"
                                    checked={Boolean(organisation?.autoCloseLeadOnBooking)}
                                    inputRef={register}
                                    name="autoCloseLeadOnBooking"
                                />
                            }
                        />
                    </AccordionDetails>
                </Accordion>

                <Accordion
                    expanded={expanded === 'security'}
                    onChange={() => setExpanded(expanded === 'security' ? '' : 'security')}
                    hidden={true}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.9rem' }} />}>
                        <Typography>Security</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {expanded === 'security' && (
                            <SecurityTab
                                control={control}
                                register={register}
                                organisation={organisation}
                                setIsLoading={setIsLoading}
                                watch={watch}
                            />
                        )}
                    </AccordionDetails>
                </Accordion>

                {/* Reports settings */}
                <Accordion
                    expanded={expanded === 'reports'}
                    onChange={() => {
                        if (expanded === 'reports') {
                            setExpanded('');
                        } else {
                            setExpanded('reports');
                        }
                    }}
                    hidden={false}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.9rem' }} />}>
                        <Typography>Reports</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <ReportsSettings
                            organisation={organisation}
                            setValue={setValue}
                            setOrganisation={setOrganisation}
                        />
                    </AccordionDetails>
                </Accordion>

                <div className={globalStyles.buttonsContainer}>
                    <Button className={globalStyles.cancelButton} onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button className={globalStyles.confirmButton} type="submit" variant="outlined">
                        Save
                    </Button>
                </div>
            </form>
            {isLoading && <LoadingScreen />}
            {isPaymentTypeModalVisible && selectedType && (
                <PaymentTypeModal
                    append={append}
                    closeModal={ClosePaymentTypeModal}
                    setUpdate={setUpdate}
                    updatePaymentType={updatePaymentType}
                    paymentType={selectedType}
                />
            )}
            {deleteModalOpen && selectedType && (
                <ConfirmModal
                    isOpen
                    setIsOpen={ClosePaymentTypeModal}
                    centerContent
                    onConfirm={handleDeletePaymentType}
                    title="Delete payment type"
                    content="Are you sure you want to delete this payment type?"
                />
            )}
            {showUploadModal && (
                <UploadPhotosModal
                    visible={showUploadModal}
                    onClose={() => setShowUploadModal(false)}
                    setUploadedPhotos={handlePhotoUpload}
                    allowedFormats={['GIF', 'PNG', 'JFIF', 'JPEG', 'JPG', 'PDF']}
                />
            )}
            {deleteImageModalOpen && (
                <ConfirmModal
                    isOpen
                    setIsOpen={setDeleteImageModalOpen}
                    centerContent
                    onConfirm={handleDeleteTreatmentPhoto}
                    title="Delete treatment image"
                    content="Are you sure you want to delete this image?"
                />
            )}
        </OpenedModal>
    );
}

export default withStyles(settingsStyles)(Settings);
